import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Bot, Mic, MessageCircle, Share2, Globe, Music2, Image as ImageIcon,
  KeyRound, Pencil, RotateCcw, FlaskConical, CheckCircle2, XCircle, Loader2, Database, FileCode2, AlertTriangle, AtSign } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { LoadState } from '@/components/ControlCenter';
import { platformConfigsService, PlatformKeyGroup, PlatformKeyRow } from '@/services/platform-configs.service';

const GROUPS: Array<{ id: PlatformKeyGroup; label: string; blurb: string; icon: React.ReactNode }> = [
  { id: 'ai',       label: 'AI providers', blurb: 'Platform fallback keys used when a company has no key of its own.', icon: <Bot size={15} /> },
  { id: 'voice',    label: 'Voice',        blurb: 'ElevenLabs and OpenAI voice for AI Studio voiceovers and transcription.', icon: <Mic size={15} /> },
  { id: 'meta',     label: 'Meta app',     blurb: 'App-level secrets for webhook signatures. Tenant WhatsApp/ad tokens live on their accounts.', icon: <Share2 size={15} /> },
  { id: 'whatsapp', label: 'WhatsApp',     blurb: 'Facebook Embedded Signup configuration and webhook relay settings.', icon: <MessageCircle size={15} /> },
  { id: 'google',   label: 'Google',       blurb: 'OAuth client + Google Ads developer token.', icon: <Globe size={15} /> },
  { id: 'tiktok',   label: 'TikTok',       blurb: 'TikTok for Business app credentials.', icon: <Music2 size={15} /> },
  { id: 'x',        label: 'X Ads',        blurb: 'X (Twitter) developer app keys for the Ads API (OAuth 1.0a) and the OAuth callback URL.', icon: <AtSign size={15} /> },
  { id: 'media',    label: 'ImageKit', blurb: 'ImageKit for uploads and AI Studio outputs.', icon: <ImageIcon size={15} /> },
];

const SourceBadge = ({ source }: { source: PlatformKeyRow['source'] }) => (
  <span className={clsx(
    'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-2xs font-semibold',
    source === 'db' ? 'bg-success/15 text-success' : source === 'env' ? 'bg-info/15 text-info' : 'bg-warning/15 text-warning',
  )}>
    {source === 'db' ? <><Database size={9} /> Database</> : source === 'env' ? <><FileCode2 size={9} /> .env</> : <><AlertTriangle size={9} /> Missing</>}
  </span>
);

export default function IntegrationsSettings() {
  const [params, setParams] = useSearchParams();
  const selected = GROUPS.some(group => group.id === params.get('group')) ? params.get('group')! : 'all';
  const [rows, setRows] = useState<PlatformKeyRow[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState<PlatformKeyRow | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; detail: string }>>({});

  const load = async () => { setLoadError(false); try { setRows(await platformConfigsService.list()); } catch { setLoadError(true); } };
  useEffect(() => { load(); }, []);

  const byGroup = useMemo(() => {
    const m = new Map<PlatformKeyGroup, PlatformKeyRow[]>();
    for (const r of rows ?? []) m.set(r.group, [...(m.get(r.group) ?? []), r]);
    return m;
  }, [rows]);

  const counts = useMemo(() => {
    const all = rows ?? [];
    return { db: all.filter((r) => r.source === 'db').length, env: all.filter((r) => r.source === 'env').length, missing: all.filter((r) => r.source === 'missing').length };
  }, [rows]);

  const save = async () => {
    if (!editing) return;
    setBusy(editing.key);
    try {
      const updated = await platformConfigsService.set(editing.key, draft);
      setRows((prev) => (prev ?? []).map((r) => (r.key === updated.key ? updated : r)));
      toast.success(`${editing.label} saved — live on every backend within a minute`);
      setEditing(null);
    } catch { /* interceptor toasts */ } finally { setBusy(null); }
  };

  const unset = async (r: PlatformKeyRow) => {
    if (!window.confirm(`Remove the database value for ${r.label}? The .env value (if any) will apply again.`)) return;
    setBusy(r.key);
    try {
      const updated = await platformConfigsService.unset(r.key);
      setRows((prev) => (prev ?? []).map((x) => (x.key === updated.key ? updated : x)));
      toast.success('Reverted to .env');
    } catch { /* interceptor toasts */ } finally { setBusy(null); }
  };

  const test = async (r: PlatformKeyRow) => {
    setBusy(`test:${r.key}`);
    try {
      const res = await platformConfigsService.test(r.key);
      setTestResult((prev) => ({ ...prev, [r.key]: res }));
      (res.ok ? toast.success : toast.error)(`${r.label}: ${res.detail}`);
      await load();
    } catch { /* interceptor toasts */ } finally { setBusy(null); }
  };

  return (
    <>
      <Topbar
        title="Platform integrations"
        breadcrumb="Platform / Integrations"
        right={rows && (
          <div className="flex items-center gap-2 text-2xs">
            <span className="rounded-full bg-success/15 px-2 py-0.5 font-semibold text-success">{counts.db} in DB</span>
            <span className="rounded-full bg-info/15 px-2 py-0.5 font-semibold text-info">{counts.env} from .env</span>
            {counts.missing > 0 && <span className="rounded-full bg-warning/15 px-2 py-0.5 font-semibold text-warning">{counts.missing} missing</span>}
          </div>
        )}
      />
      <div className="admin-page space-y-4">
        <nav aria-label="Integration providers" className="flex gap-1 overflow-x-auto border-b border-border pb-3">
          {[{ id: 'all', label: 'All integrations', icon: <Globe size={15} /> }, ...GROUPS].map(group => <button key={group.id} type="button" aria-pressed={selected === group.id} onClick={() => setParams(group.id === 'all' ? {} : { group: group.id })} className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${selected === group.id ? 'bg-primary-soft text-primary' : 'text-text-secondary hover:bg-surface-alt'}`}>{group.icon}{group.label}</button>)}
        </nav>
        <div className="rounded-xl border border-border bg-surface p-4 text-xs leading-relaxed text-text-secondary">
          <strong className="text-text-primary">Manage your platform connections in one place.</strong>{' '}
          Saved settings override server defaults and take effect within a minute. Secrets are encrypted and displayed masked. Company account credentials are managed from each company's account.
        </div>
        <LoadState loading={!rows && !loadError} error={loadError} hasData={!!rows} retry={load} />
        {rows && GROUPS.filter((g) => byGroup.has(g.id) && (selected === 'all' || selected === g.id)).map((g, gi) => (
          <section key={g.id} className="fade-up overflow-hidden rounded-xl border border-border bg-surface" style={{ animationDelay: `${gi * 40}ms` }}>
            <header className="flex items-start gap-3 border-b border-border px-5 py-3.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">{g.icon}</span>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-text-primary">{g.label}</h3>
                <p className="text-2xs text-text-muted">{g.blurb}</p>
              </div>
            </header>
            <ul className="divide-y divide-border">
              {(byGroup.get(g.id) ?? []).map((r) => {
                const t = testResult[r.key];
                return (
                  <li key={r.key} className="flex flex-wrap items-center gap-3 px-5 py-3">
                    <div className="min-w-[220px] flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text-primary">{r.label}</span>
                        <SourceBadge source={r.source} />
                        {r.isSecret && <KeyRound size={11} className="text-text-muted" />}
                      </div>
                      <div className="mt-0.5 font-mono text-2xs text-text-muted">{r.key}</div>
                      {r.description && <div className="mt-0.5 text-2xs text-text-secondary">{r.description}</div>}
                    </div>
                    <div className="min-w-[180px] font-mono text-xs text-text-secondary">
                      {r.disabled ? <span className="text-text-muted">Disabled</span> : r.value ?? <span className="text-warning">not set</span>}
                    </div>
                    {t && (
                      <span className={clsx('inline-flex items-center gap-1 text-2xs', t.ok ? 'text-success' : 'text-danger')}>
                        {t.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {t.detail}
                      </span>
                    )}
                    <div className="ml-auto flex items-center gap-1.5">
                      {r.test && (
                        <Button variant="secondary" size="sm" disabled={!!busy || !r.value} onClick={() => test(r)}>
                          {busy === `test:${r.key}` ? <Loader2 size={12} className="animate-spin" /> : <FlaskConical size={12} />}
                          <span className="ml-1">Test</span>
                        </Button>
                      )}
                      {r.source === 'db' && (
                        <Button variant="secondary" size="sm" disabled={!!busy} onClick={() => unset(r)} title="Remove DB value, fall back to .env">
                          <RotateCcw size={12} />
                        </Button>
                      )}
                      <Button size="sm" disabled={!!busy} onClick={() => { setEditing(r); setDraft(r.isSecret ? '' : (r.value ?? '')); }}>
                        <Pencil size={12} /><span className="ml-1">{r.source === 'missing' ? 'Set' : 'Edit'}</span>
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => !busy && setEditing(null)} title={editing ? `Set ${editing.label}` : ''}>
        {editing && (
          <div className="space-y-3">
            <p className="text-xs text-text-muted">
              Key <code className="font-mono">{editing.key}</code>.{' '}
              {editing.isSecret ? 'Stored encrypted; shown masked afterwards.' : 'Not a secret — stored as-is.'}
              {editing.source === 'env' && ' Saving here overrides the .env value.'}
            </p>
            <FormField label="Value" required>
              <Input
                type={editing.isSecret ? 'password' : 'text'}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={editing.isSecret ? 'Paste the new key' : ''}
                autoFocus
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setEditing(null)} disabled={!!busy}>Cancel</Button>
              <Button onClick={save} disabled={!!busy || !draft.trim()}>{busy ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
